/**
 * Charts and formatters for the ATTOM property dossier
 * (pages/collateral/PropertyProfile.tsx).
 *
 * The packages/ui LineChart takes no props and renders demo data, and
 * AreaChart draws one index-spaced series inside a bordered card. These
 * marks need a time scale, direct labels and no card, so they are drawn
 * here. Every mark is percent-positioned HTML or an SVG with token classes
 * and a non-scaling stroke: nothing is measured, so every width and jsdom
 * render the same DOM.
 */

export function pct(v: number, lo: number, hi: number): number {
  if (hi === lo) return 50;
  return Math.min(100, Math.max(0, ((v - lo) / (hi - lo)) * 100));
}

// Three significant digits for every compact figure, so figures on one page
// agree: $863K, $1.74M, $7.7K-$11.5K. A fraction-digit cap let each value
// drop trailing zeros on its own ($863.1K beside $1.74M).
const USD_COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumSignificantDigits: 3,
});

export function usdCompact(v: number): string {
  return USD_COMPACT.format(v);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function parseDate(iso: string): Date {
  return new Date(DATE_ONLY.test(iso) ? `${iso}T00:00:00Z` : iso);
}

// A date-only string is a calendar date: it formats in UTC so 2006-02-09
// reads Feb 9, 2006 in every time zone. A timestamp formats in local time.
export function fmtDateUS(iso: string): string {
  const d = parseDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(DATE_ONLY.test(iso) ? { timeZone: 'UTC' } : {}),
  }).format(d);
}

export function fmtTime(ms: number): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
}

export function yearFraction(iso: string): number | null {
  const d = parseDate(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  return y + (d.getTime() - Date.UTC(y, 0, 1)) / (365 * 86_400_000);
}

// Rounds a zero-based domain up to a step of 1, 2, 2.5 or 5 times a power of ten.
export function niceScale(max: number, maxTicks: number): { domainMax: number; ticks: number[] } {
  if (!(max > 0)) return { domainMax: 1, ticks: [0, 1] };
  const rough = max / maxTicks;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough) ?? 10 * mag;
  const domainMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let i = 0; i * step <= domainMax + step / 2; i += 1) ticks.push(i * step);
  return { domainMax, ticks };
}

// A label near either edge aligns inward so it never leaves the column.
function alignX(x: number): string {
  if (x < 15) return '';
  if (x > 85) return '-translate-x-full';
  return '-translate-x-1/2';
}

/**
 * One horizontal scale for the figures a lender compares: the AVM with its
 * range band, the assessed value (filled square) and the last sale (ring).
 * Every mark is labeled directly, so the domain need not start at zero.
 */
export function ValueRule({
  avm,
  low,
  high,
  anchors,
}: {
  avm?: number;
  low?: number;
  high?: number;
  anchors: Array<{ key: string; label: string; value: number; shape: 'square' | 'ring' }>;
}) {
  const finite = (v: number | undefined): v is number => typeof v === 'number' && Number.isFinite(v);
  const vals = [low, high, avm, ...anchors.map((a) => a.value)].filter(finite);
  if (new Set(vals).size < 2) return null;
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const pad = (hi - lo) * 0.04;
  const x = (v: number) => pct(v, lo - pad, hi + pad);

  const placed = anchors
    .filter((a) => finite(a.value))
    .map((a) => ({ ...a, x: x(a.value) }))
    .sort((a, b) => a.x - b.x)
    .map((a, i, all) => ({ ...a, tier: i > 0 && a.x - all[i - 1].x < 18 ? 1 : 0 }));
  const twoTiers = placed.some((a) => a.tier === 1);
  const hasBand = finite(low) && finite(high);

  const parts: string[] = [];
  if (finite(avm)) {
    parts.push(
      `ATTOM automated valuation ${usdCompact(avm)}${hasBand ? `, range ${usdCompact(low as number)} to ${usdCompact(high as number)}` : ''}.`,
    );
  }
  for (const a of placed) parts.push(`${a.label} ${usdCompact(a.value)}.`);

  return (
    <div role="img" aria-label={parts.join(' ')} className="mt-5">
      {hasBand && (
        <div aria-hidden="true" className="relative h-4">
          {x(high as number) - x(low as number) < 22 ? (
            <span
              className={`absolute top-0 whitespace-nowrap font-mono text-caption-sm tabular-nums text-tertiary ${alignX((x(low as number) + x(high as number)) / 2)}`}
              style={{ left: `${(x(low as number) + x(high as number)) / 2}%` }}
            >
              {`${usdCompact(low as number)}–${usdCompact(high as number)}`}
            </span>
          ) : (
            [low as number, high as number].map((v) => (
              <span
                key={v}
                className={`absolute top-0 whitespace-nowrap font-mono text-caption-sm tabular-nums text-tertiary ${alignX(x(v))}`}
                style={{ left: `${x(v)}%` }}
              >
                {usdCompact(v)}
              </span>
            ))
          )}
        </div>
      )}
      <div aria-hidden="true" className="relative h-8">
        <span className="absolute inset-x-0 top-4 h-px bg-recessed" />
        {hasBand && (
          <span
            className="absolute top-3 h-2 bg-primary/15"
            style={{ left: `${x(low as number)}%`, width: `${x(high as number) - x(low as number)}%` }}
          />
        )}
        {finite(avm) && (
          <span className="absolute top-1.5 h-5 w-0.5 -translate-x-1/2 bg-primary" style={{ left: `${x(avm)}%` }} />
        )}
        {placed.map((a) => (
          <span
            key={a.key}
            className={
              a.shape === 'square'
                ? 'absolute top-3 h-2 w-2 -translate-x-1/2 bg-secondary'
                : 'absolute top-3 h-2 w-2 -translate-x-1/2 rounded-full border-2 border-secondary bg-canvas'
            }
            style={{ left: `${a.x}%` }}
          />
        ))}
      </div>
      <div aria-hidden="true" className={`relative ${twoTiers ? 'h-[4.5rem]' : 'h-9'}`}>
        {placed.map((a) => (
          <span
            key={a.key}
            className={`absolute whitespace-nowrap font-mono text-caption-sm tabular-nums ${alignX(a.x)} ${a.tier ? 'top-9' : 'top-0'}`}
            style={{ left: `${a.x}%` }}
          >
            <span className="block text-tertiary">{a.label}</span>
            <span className="block text-secondary">{usdCompact(a.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** A decorative trend line; the caller carries the values as text. */
export function InlineSparkline({ values, className = '' }: { values: number[]; className?: string }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const yOf = (v: number) => (span === 0 ? 16 : 32 - ((v - min + span * 0.1) / (span * 1.2)) * 32);
  const xOf = (i: number) => (i / (values.length - 1)) * 100;
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(2)} ${yOf(v).toFixed(2)}`).join(' ');
  const lastY = yOf(values[values.length - 1]);
  return (
    <span aria-hidden="true" className={`relative block ${className}`}>
      <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 32" preserveAspectRatio="none">
        <path d={d} fill="none" className="stroke-primary" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      </svg>
      <span
        className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
        style={{ left: '100%', top: `${(lastY / 32) * 100}%` }}
      />
    </span>
  );
}

/**
 * Value over time on a zero-based axis: the assessed roll as a step line,
 * sales as rings, and the current AVM as a dot with its high-low whisker.
 */
export function ValueHistoryChart({
  assessments,
  sales,
  avm,
  avmHistory,
  summary,
}: {
  assessments: Array<{ x: number; total: number }>;
  sales: Array<{ x: number; amount: number; label: string }>;
  avm?: { x: number; value: number; low?: number; high?: number };
  avmHistory: Array<{ x: number; value: number }>;
  summary: string;
}) {
  const xs = [...assessments.map((a) => a.x), ...sales.map((s) => s.x), ...avmHistory.map((h) => h.x)];
  if (avm) xs.push(avm.x);
  const ys = [
    ...assessments.map((a) => a.total),
    ...sales.map((s) => s.amount),
    ...avmHistory.map((h) => h.value),
  ];
  if (avm) ys.push(avm.high ?? avm.value);
  if (xs.length < 2) return null;

  const minX = Math.min(...xs) - 0.5;
  const maxX = Math.max(...xs) + 0.5;
  const { domainMax, ticks } = niceScale(Math.max(...ys), 4);
  const px = (x: number) => pct(x, minX, maxX);
  const py = (y: number) => 100 - pct(y, 0, domainMax);

  const sorted = [...assessments].sort((a, b) => a.x - b.x);
  const step = sorted
    .map((a, i) => {
      const X = px(a.x) * 10;
      const Y = py(a.total);
      return i === 0 ? `M${X.toFixed(1)} ${Y.toFixed(2)}` : `H${X.toFixed(1)} V${Y.toFixed(2)}`;
    })
    .join(' ');
  const hist = [...avmHistory]
    .sort((a, b) => a.x - b.x)
    .map((h, i) => `${i === 0 ? 'M' : 'L'}${(px(h.x) * 10).toFixed(1)} ${py(h.value).toFixed(2)}`)
    .join(' ');
  const lastAssessed = sorted[sorted.length - 1];

  return (
    <figure className="m-0">
      <div role="img" aria-label={summary} className="relative h-44 lg:h-72">
        <div aria-hidden="true" className="absolute bottom-0 left-0 top-4 w-10">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute right-1 -translate-y-1/2 font-mono text-caption-xs tabular-nums text-tertiary"
              style={{ top: `${py(t)}%` }}
            >
              {t === 0 ? '0' : usdCompact(t)}
            </span>
          ))}
        </div>
        <div aria-hidden="true" className="absolute bottom-0 left-11 right-2 top-4">
          <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 1000 100" preserveAspectRatio="none">
            {ticks.map((t) => (
              <line
                key={t}
                x1={0}
                x2={1000}
                y1={py(t)}
                y2={py(t)}
                className={t === 0 ? 'stroke-tertiary' : 'stroke-recessed'}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {sorted.length > 1 && (
              <path d={step} fill="none" className="stroke-secondary" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
            )}
            {avmHistory.length > 1 && (
              <path d={hist} fill="none" className="stroke-primary" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
            )}
            {avm && avm.low != null && avm.high != null && (
              <line
                x1={px(avm.x) * 10}
                x2={px(avm.x) * 10}
                y1={py(avm.high)}
                y2={py(avm.low)}
                className="stroke-primary"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {sales.map((s) => (
            <span key={`sale-${s.x}`}>
              <span
                className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-secondary bg-canvas"
                style={{ left: `${px(s.x)}%`, top: `${py(s.amount)}%` }}
              />
              <span
                className={`absolute -translate-y-full whitespace-nowrap pb-1.5 font-mono text-caption-sm tabular-nums text-tertiary ${alignX(px(s.x))}`}
                style={{ left: `${px(s.x)}%`, top: `${py(s.amount)}%` }}
              >
                {`${s.label} ${usdCompact(s.amount)}`}
              </span>
            </span>
          ))}
          {lastAssessed && (
            <span
              className={`absolute -translate-y-full whitespace-nowrap pb-1 font-mono text-caption-sm tabular-nums text-secondary ${alignX(px(lastAssessed.x))}`}
              style={{ left: `${px(lastAssessed.x)}%`, top: `${py(lastAssessed.total)}%` }}
            >
              Assessed
            </span>
          )}
          {avm && (
            <>
              <span
                className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
                style={{ left: `${px(avm.x)}%`, top: `${py(avm.value)}%` }}
              />
              <span
                className={`absolute -translate-y-full whitespace-nowrap pb-1.5 font-mono text-caption-sm font-semibold tabular-nums text-primary ${alignX(px(avm.x))}`}
                style={{ left: `${px(avm.x)}%`, top: `${py(avm.high ?? avm.value)}%` }}
              >
                {`AVM ${usdCompact(avm.value)}`}
              </span>
            </>
          )}
        </div>
      </div>
      <div
        aria-hidden="true"
        className="ml-11 mr-2 mt-1 flex justify-between font-mono text-caption-xs tabular-nums text-tertiary"
      >
        <span>{Math.floor(minX + 0.5)}</span>
        <span>{Math.floor(maxX - 0.5)}</span>
      </div>
    </figure>
  );
}
